// src/components/VoiceFormFiller.jsx
// "Fill this stage by voice" — unlike VoiceMicButton (which dictates into one
// field), this records a single passage covering many fields at once ("name
// is Ramesh, mobile 98765..., age 30, male"), transcribes + translates it to
// English, then asks the AI to extract a value per field from the stage's
// own field schema and hands the whole set back via onFields so the caller
// can merge them into its form state in one shot.
import { useState } from 'react';
import { authFetch } from '../api';
import { useSegmentedRecorder } from '../hooks/useSegmentedRecorder';
import { transcribeSegments } from '../utils/sarvam';
import { getVoiceLanguage } from '../lib/voiceLanguages';
import { useAiAvailability } from '../features/ai-assist/useAiAvailability';

export interface VoiceField {
  key: string;
  label: string;
  type: string;
  options?: readonly string[];
  /** `choice-matrix` only — each row is its own subject with its own allowed
   * options, which a flat `options[]` can't express. See
   * backend/routes/ai.js's `describeField` and utils/extractedFields.js. */
  rows?: ReadonlyArray<{ key: string; label: string; options: readonly string[] }>;
}

interface VoiceFormFillerProps {
  fields: VoiceField[];
  // A "vitals"/"due-checklist" field's extracted value is actually an object
  // keyed by sub-label at runtime (see backend/routes/ai.js's describeField),
  // not a plain string like every other field type — callers that use those
  // field types (PreConsultStage, VitalsStage) cast through this at the call
  // site rather than widening the type for every other caller.
  onFields?: (data: Record<string, string>) => void;
  label?: string;
  /** What to say — replaces the generic "Speak naturally…" line, e.g.
   *  "BP 130 by 80, pulse 92, temp 37.8, sats 96 on room air". */
  hint?: string;
}

// Counts answers that actually carry something — an object/array answer
// (vitals, due-checklist, choice-matrix, checkbox-group) counts once.
function countAnswered(data: Record<string, unknown>): number {
  return Object.values(data).filter(v => {
    if (v == null || v === '') return false;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === 'object') return Object.keys(v).length > 0;
    return true;
  }).length;
}

export default function VoiceFormFiller({ fields, onFields, label = '🎙 Fill this form by voice', hint }: VoiceFormFillerProps) {
  // AI off (§4.4): this also calls /api/ai/extract-fields, an LLM capability,
  // not just speech-to-text — hide the whole control, same as VoiceMicButton.
  const { loading, aiEnabled } = useAiAvailability();
  const [processing, setProcessing] = useState(false);
  const [error, setError]         = useState('');
  // Feedback after a fill: without it a dictation that matched nothing looks
  // identical to one that worked, and the user has to hunt the form to tell.
  const [result, setResult]       = useState<string>('');
  const { recording, timeElapsed, start, stopAndWait, error: recError } = useSegmentedRecorder();

  if (loading || !aiEnabled) return null;

  const formatTime = (s: number) => `${Math.floor(s/60).toString().padStart(2,'0')}:${(s%60).toString().padStart(2,'0')}`;

  const handleStop = async () => {
    const segments = await stopAndWait();
    if (!segments.length) return;
    setProcessing(true); setError(''); setResult('');
    try {
      const lang = getVoiceLanguage();
      let text = await transcribeSegments(segments, lang, 'form-fill');
      if (!text) { setError('No speech was detected. Try again and speak clearly.'); setProcessing(false); return; }
      if (lang !== 'en-IN') {
        const tRes  = await authFetch('/api/voice/translate', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text, source_language_code: lang, target_language_code: 'en-IN' }),
        });
        const tData = await tRes.json();
        if (tRes.ok) text = tData.translated_text;
      }
      const res  = await authFetch('/api/ai/extract-fields', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transcript: text, fields }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Field extraction failed');
      const n = countAnswered(data);
      setResult(n
        ? `✓ Filled ${n} field${n === 1 ? '' : 's'} — check them before saving.`
        : 'Nothing in that recording matched these fields. Try again, naming each item.');
      onFields?.(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
    setProcessing(false);
  };

  return (
    <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', padding:'10px 14px', background:'#F0FDF4', border:'1px solid #A7F3D0', borderRadius:'var(--radius)', marginBottom:16 }}>
      {!recording ? (
        <button type="button" className="btn-primary" style={{ padding:'8px 16px', fontSize:13, minHeight:40 }} onClick={() => { setResult(''); start(); }} disabled={processing}>
          {processing ? '⏳ Filling fields…' : label}
        </button>
      ) : (
        <button type="button" onClick={handleStop}
          style={{ padding:'8px 16px', fontSize:13, minHeight:40, background:'#DC2626', color:'#fff', border:'none', borderRadius:'var(--radius)', cursor:'pointer', fontWeight:600 }}>
          ⏹ Stop · {formatTime(timeElapsed)}
        </button>
      )}
      <span style={{ fontSize:11, color:'#065F46', flex:'1 1 220px' }}>
        {recording ? 'Listening… tap Stop when you are done.'
          : hint ? <>Say it in one go, e.g. <i>“{hint}”</i></>
          : 'Speak naturally — mention as many details as you like; matching fields fill in automatically.'}
      </span>
      {result && !recording && !processing && <span style={{ fontSize:11.5, fontWeight:600, color: result.startsWith('✓') ? '#065F46' : '#92400E', flexBasis:'100%' }}>{result}</span>}
      {(error || recError) && <span style={{ fontSize:11, color:'#DC2626' }}>{error || recError}</span>}
    </div>
  );
}
